/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { FieldPath, PathValue, UseFormReturn } from "react-hook-form";

import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TableCell } from "@/components/ui/table";

import { dates } from "@/lib/dates";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

export const TableCellDatePicker = <T extends Record<string, unknown>>({
  disabled,
  fieldName,
  form,
  handleOnBlur,
  index,
  listName,
  rowId,
  showError,
}: {
  disabled?: boolean;
  fieldName: string;
  form: UseFormReturn<T>;
  handleOnBlur: (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => void;
  index: number;
  listName: string;
  rowId: number;
  showError?: (
    form: UseFormReturn<T>,
    fieldName: FieldPath<T> | string,
  ) => string;
}) => {
  const fieldNamePath = `${listName}.${index}.${fieldName}` as FieldPath<T>;

  const initialDate = form.getValues(fieldNamePath)
    ? new Date(form.getValues(fieldNamePath) as string)
    : undefined;

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(initialDate);

  return (
    <TableCell className={cn("p-1", showError?.(form, fieldNamePath))}>
      <FormField
        control={form.control}
        name={fieldNamePath}
        render={({ field }) => {
          return (
            <FormItem>
              <FormControl>
                <Row className="items-center">
                  <Input
                    {...field}
                    id={`cell-${fieldName}-${index}`}
                    className="w-32 rounded-sm border-transparent bg-transparent hover:border-slate-300 focus:border-slate-300"
                    disabled={disabled}
                    onBlur={() => {
                      void form
                        .trigger(fieldNamePath, { shouldFocus: false })
                        .then((ok) => {
                          if (!ok) return;
                          handleOnBlur(
                            rowId,
                            fieldName,
                            field.value as string,
                            "date",
                          );
                          mixpanelUtils.trackInlineEdit(
                            fieldName,
                            getScreenFromPathname(location.pathname),
                          );
                        });
                    }}
                    placeholder="-"
                    value={field.value as string}
                  />
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        disabled={disabled}
                        variant="ghost"
                        className="border border-transparent focus:border-slate-300"
                      >
                        <CalendarIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(date) => {
                          setDate(date);
                          const formattedDate = dates.formatIsoDateForUser(
                            date?.toISOString(),
                          );
                          form.setValue(
                            fieldNamePath,
                            formattedDate as PathValue<T, FieldPath<T>>,
                            {
                              shouldValidate: true,
                            },
                          );
                          handleOnBlur(
                            rowId,
                            fieldName,
                            date?.toISOString() ?? null,
                            "date",
                          );
                          mixpanelUtils.trackInlineEdit(
                            fieldName,
                            getScreenFromPathname(location.pathname),
                          );
                          setOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </Row>
              </FormControl>
            </FormItem>
          );
        }}
      />
    </TableCell>
  );
};
